import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FolderOpenIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { laufwerkStatus, type LaufwerkStatus } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { indexFortschritt, zahl } from '../utils/laufwerk';

/** Startseite: Frage → Chat („Fragen"); für Administratoren eine Kurzinfo zum Index (oder der Hinweis, noch ein Laufwerk zu verbinden). */
export default function Start() {
  const { user, isAdmin } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [frage, setFrage] = useState('');
  const [status, setStatus] = useState<LaufwerkStatus | null>(null);
  const [statusGeladen, setStatusGeladen] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let weg = false;
    laufwerkStatus()
      .then((s) => { if (!weg) setStatus(s); })
      .catch(() => { /* Kurzinfo ist nur eine Zugabe — ohne Status bleibt sie weg */ })
      .finally(() => { if (!weg) setStatusGeladen(true); });
    return () => { weg = true; };
  }, [isAdmin]);

  const fragen = (e: React.FormEvent) => {
    e.preventDefault();
    const q = frage.trim();
    if (q) navigate(`/fragen?frage=${encodeURIComponent(q)}`);
  };

  const keinConnector = !!status && status.connectors.filter((c) => !c.widerrufen).length === 0;
  const f = status ? indexFortschritt(status.zahlen, status.gesamt) : null;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-ps-dark-900">Willkommen{user?.name ? `, ${user.name}` : ''}</h1>
        <p className="mt-1 text-sm text-ps-dark-500">{branding.untertitel}</p>
      </div>

      <form onSubmit={fragen} className="rounded-lg border border-ps-dark-200 bg-white p-4 shadow-sm sm:p-5">
        <label htmlFor="start-suche" className="mb-2 block font-medium text-ps-dark-900">Was möchten Sie wissen?</label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative min-w-0 flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-ps-dark-400" />
            <input id="start-suche" type="search" value={frage} onChange={(e) => setFrage(e.target.value)}
              placeholder="z. B. Welche Kündigungsfrist hat unser Mietvertrag?" className="input-field pl-10" />
          </div>
          <button type="submit" disabled={!frage.trim()} className="btn-primary">Fragen</button>
        </div>
        <p className="mt-2 text-xs text-ps-dark-500">
          Die Antwort kommt aus Ihren Dateien — mit Quelle zu jeder Angabe. Oder <Link to="/dateien" className="font-medium text-primary-700 hover:underline">alle Ordner durchblättern</Link>.
        </p>
      </form>

      {isAdmin && !statusGeladen && <div className="card space-y-2"><div className="shimmer h-3 w-1/3 rounded" /><div className="shimmer h-3 w-1/2 rounded" /></div>}

      {isAdmin && keinConnector && (
        <div className="flex items-start gap-4 rounded-lg border border-ps-dark-200 bg-white p-5 shadow-sm">
          <FolderOpenIcon className="h-8 w-8 shrink-0 text-primary-500" />
          <div className="min-w-0">
            <h2 className="font-medium text-ps-dark-900">Noch kein Laufwerk verbunden</h2>
            <p className="mt-1 text-sm text-ps-dark-600">
              Sobald ein Laufwerk oder eine Quelle (Google Drive, OneDrive, Dropbox, WebDAV, SFTP, E-Mail …) angebunden
              ist, können Sie hier in Ihren Dateien suchen — immer mit Quelle und immer auf dem aktuellen Stand.
            </p>
            <Link to="/laufwerk" className="mt-2 inline-block text-sm font-medium text-primary-700 hover:underline">Laufwerk oder Quelle verbinden →</Link>
          </div>
        </div>
      )}

      {isAdmin && status && f && !keinConnector && (
        <div className="flex items-start gap-4 rounded-lg border border-ps-dark-200 bg-white p-5 shadow-sm">
          <FolderOpenIcon className="h-8 w-8 shrink-0 text-primary-500" />
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-ps-dark-900">
              <span className="numerical">{zahl(f.fertig)}</span> von <span className="numerical">{zahl(f.nenner)}</span> Dateien durchsuchbar
            </h2>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-ps-dark-100" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={f.prozent} aria-label="Index-Fortschritt">
              <div className="h-full rounded-full bg-primary-500" style={{ width: `${f.prozent}%` }} />
            </div>
            <p className="mt-2 text-sm text-ps-dark-600">
              {f.wartend > 0 ? `Der Index wird noch aufgebaut (${zahl(f.wartend)} ${f.wartend === 1 ? 'Datei' : 'Dateien'} in Arbeit). ` : ''}
              <Link to="/laufwerk" className="font-medium text-primary-700 hover:underline">Details zum Laufwerk →</Link>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
